import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, MapPin } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { useGps } from "@/features/gps/useGps";
import { GpsIndicator } from "@/features/gps/GpsIndicator";
import { Card, Button, Spinner, EmptyState } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { queueItem } from "@/lib/offline";
import { FormField } from "./FormField";
import {
  validateForm, isVisible,
  type FormSchema, type FormValues, type ValidationError,
} from "./schema";
import type { SignaturePadHandle } from "@/features/signature/SignaturePad";
import type { WatermarkContext } from "@/features/photos/capture";

// =============================================================================
// FormRunner — the dynamic form screen
// =============================================================================
// Route: /forms/run/:templateCode   (e.g. /forms/run/TAKE5)
// Reads the form_templates row matching templateCode + highest version,
// renders its sections step-by-step (mobile-first), submits to
// form_submissions.
//
// Every submission captures:
//   · GPS lat/lng + accuracy
//   · captured_at (device) and received_at (server)
//   · submitted_by_employee_id (from useRequireAuth())
//   · device_info: ua, platform, online status
// =============================================================================

export default function FormRunner() {
  const { templateCode } = useParams<{ templateCode: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const gps = useGps(true);
  const signatureRefs = useRef<Record<string, SignaturePadHandle | null>>({});

  // -- Load the template -----------------------------------------------------
  const { data: template, isLoading } = useQuery({
    queryKey: ["form_template", templateCode],
    enabled: !!templateCode,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("form_templates")
        .select("template_id, template_code, display_name, form_kind, version, schema_json, requires_signature, requires_gps, requires_photo, min_sign_on_count")
        .eq("template_code", templateCode)
        .eq("is_active", true)
        .order("version", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const schema = (template?.schema_json ?? { sections: [] }) as FormSchema;
  const sections = schema.sections ?? [];

  // -- State -----------------------------------------------------------------
  const [step, setStep] = useState(0);
  const [values, setValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [submitted, setSubmitted] = useState<{ submission_number?: string; offline: boolean } | null>(null);

  // -- Watermark context for any photo fields --------------------------------
  const wmCtx: WatermarkContext = useMemo(() => {
    const project_code = (values.project_code as string) ?? undefined;
    const site_code    = (values.site_code    as string) ?? undefined;
    const employee_name = user
      ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || user.login_email
      : undefined;
    return { project_code, site_code, employee_name };
  }, [values.project_code, values.site_code, user]);

  // -- Validate current section on-the-fly ----------------------------------
  const currentSectionErrors = useMemo(() => {
    if (!sections[step]) return [];
    return errors.filter(e => e.sectionIndex === step);
  }, [errors, step, sections]);

  // -- Submit ----------------------------------------------------------------
  const submitMut = useMutation({
    mutationFn: async () => {
      if (!template || !user) throw new Error("not_ready");

      // Full validation before submit
      const allErrors = validateForm(schema, values);
      if (allErrors.length > 0) {
        setErrors(allErrors);
        // Jump to first section with an error
        const firstErrorSection = Math.min(...allErrors.map(e => e.sectionIndex));
        setStep(firstErrorSection);
        throw new Error("validation_failed");
      }

      // Collect signature SVGs from the pads
      const withSignatures: FormValues = { ...values };
      for (const [key, pad] of Object.entries(signatureRefs.current)) {
        if (pad && !pad.isEmpty()) withSignatures[key] = { svg: pad.getSvg(), meta: pad.getMeta() };
      }

      const payload = {
        template_id: template.template_id,
        project_id:  (withSignatures.project_id as string) ?? null,
        site_id:     (withSignatures.site_id    as string) ?? null,
        submitted_by_employee_id: user.employee_id,
        submitted_by_user_id: user.user_id,
        field_values: withSignatures,
        captured_at:  new Date().toISOString(),
        gps_lat:      gps.fix?.latitude,
        gps_lng:      gps.fix?.longitude,
        gps_accuracy_m: gps.fix?.accuracy_m,
        device_info: {
          ua: navigator.userAgent,
          platform: navigator.platform,
          online: navigator.onLine,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
        },
      };

      if (!navigator.onLine) {
        await queueItem("form_submission", payload);
        return { offline: true };
      }

      const { data, error } = await supabase
        .from("form_submissions")
        .insert(payload)
        .select("submission_id, submission_number")
        .single();
      if (error) throw error;
      return { offline: false, ...data };
    },
    onSuccess: (res) => {
      // Specialised post-submit for JSEA: hop straight to the sign-on screen
      // so the crew can queue up to sign on. The submission record itself
      // is already saved — it's now a team activity.
      if (
        template?.form_kind === "jsea" &&
        !(res as any).offline &&
        (res as any).submission_id
      ) {
        navigate(`/forms/signon/${(res as any).submission_id}`);
        return;
      }
      setSubmitted({ submission_number: (res as any).submission_number, offline: (res as any).offline });
      qc.invalidateQueries({ queryKey: ["form_submissions"] });
    },
  });

  // -- Navigation -----------------------------------------------------------
  function nextStep() {
    const all = validateForm(schema, values);
    const sectionErrors = all.filter(e => e.sectionIndex === step);
    if (sectionErrors.length > 0) {
      setErrors(sectionErrors);
      return;
    }
    setErrors([]);
    setStep(s => Math.min(sections.length - 1, s + 1));
  }
  function prevStep() {
    setErrors([]);
    setStep(s => Math.max(0, s - 1));
  }

  // -- Render ----------------------------------------------------------------
  if (isLoading) {
    return <div className="py-20 flex justify-center"><Spinner size={20} /></div>;
  }

  if (!template) {
    return (
      <div className="max-w-md mx-auto p-6">
        <Card><EmptyState title="Form not found" description={`No active template with code "${templateCode}".`} /></Card>
        <div className="mt-4 text-center">
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate("/forms")}>Forms</Button>
        </div>
      </div>
    );
  }

  // Success screen
  if (submitted) {
    return (
      <div className="max-w-md mx-auto p-4 pt-16 text-center">
        <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-5 ${submitted.offline ? "bg-warn/12" : "bg-ok/12"}`}>
          <CheckCircle2 size={40} className={submitted.offline ? "text-warn" : "text-ok"} />
        </div>
        <h2 className="text-xl font-semibold">{template.display_name} submitted</h2>
        <div className="text-text-muted text-[13px] mt-2 leading-relaxed">
          {submitted.offline ? (
            <>Queued offline.<br/>Will sync automatically when you're back online.</>
          ) : (
            <>
              <span className="font-mono">{submitted.submission_number}</span>
              <br />
              GPS tagged · signed by {user?.first_name} {user?.last_name}
            </>
          )}
        </div>
        <div className="mt-8 flex flex-col gap-2">
          <Button onClick={() => navigate("/forms")}>Done</Button>
          <Button
            variant="ghost"
            onClick={() => {
              setSubmitted(null);
              setValues({});
              setStep(0);
              signatureRefs.current = {};
            }}
          >
            Submit another
          </Button>
        </div>
      </div>
    );
  }

  const section = sections[step];
  const isLast = step === sections.length - 1;
  const progress = sections.length > 0 ? ((step + 1) / sections.length) * 100 : 100;

  return (
    <div className="max-w-lg mx-auto p-4 pb-32">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[11px] font-mono tracking-wider2 uppercase text-text-muted">Form · {template.template_code}</div>
          <h1 className="text-[22px] font-semibold tracking-tight mt-0.5">{template.display_name}</h1>
        </div>
        <div className="bg-accent/12 text-accent px-2 py-1 rounded text-[11px] font-mono">
          STEP {step + 1}/{sections.length}
        </div>
      </div>

      <div className="flex items-center gap-3 text-[11px] text-text-muted mb-4">
        <GpsIndicator gps={gps} />
        {wmCtx.project_code && <span className="font-mono">· {wmCtx.project_code}</span>}
      </div>

      {/* Progress */}
      <div className="h-1 bg-ink-raised rounded-full overflow-hidden mb-5">
        <div className="h-full bg-accent transition-[width] duration-300" style={{ width: `${progress}%` }} />
      </div>

      {/* Section content */}
      <Card>
        <div className="p-5">
          {section ? (
            <>
              <div className="text-[15px] font-medium text-text mb-1">{section.title}</div>
              {section.description && <div className="text-[12px] text-text-muted mb-4">{section.description}</div>}
              <div className="space-y-5 mt-4">
                {section.fields
                  .filter(f => isVisible(f, values))
                  .map(field => (
                    <FormField
                      key={field.key}
                      field={field}
                      value={values[field.key]}
                      onChange={(v) => setValues(prev => ({ ...prev, [field.key]: v }))}
                      error={currentSectionErrors.find(e => e.key === field.key)?.message}
                      gps={gps.fix}
                      ctx={wmCtx}
                      signatureRefs={signatureRefs}
                    />
                  ))}
              </div>
            </>
          ) : (
            <div className="text-text-muted text-[13px]">No content in this form. Check the schema.</div>
          )}
        </div>
      </Card>

      {/* Fixed footer actions */}
      <StickyBottomBar className="p-3 flex gap-2 justify-center">
        <div className="flex gap-2 max-w-lg w-full mx-auto">
          <Button
            variant="secondary"
            size="lg"
            onClick={() => (step === 0 ? navigate("/forms") : prevStep())}
          >
            {step === 0 ? "Cancel" : "Back"}
          </Button>
          {!isLast ? (
            <Button size="lg" icon={ArrowRight} onClick={nextStep} className="flex-1 justify-center">
              Next
            </Button>
          ) : (
            <Button
              size="lg"
              icon={submitMut.isPending ? undefined : CheckCircle2}
              onClick={() => submitMut.mutate()}
              disabled={submitMut.isPending}
              className="flex-1 justify-center"
            >
              {submitMut.isPending ? <Loader2 size={14} className="animate-spin" /> : "Submit"}
            </Button>
          )}
        </div>
      </StickyBottomBar>

      {submitMut.isError && submitMut.error?.message !== "validation_failed" && (
        <div className="mt-3 bg-danger/10 border border-danger/20 rounded px-3 py-2 text-[12px] text-danger">
          {String(submitMut.error?.message ?? submitMut.error)}
        </div>
      )}
    </div>
  );
}
